import { Controller } from "@hotwired/stimulus"

// The Write screen (PRT_Press_Release_Plan.md; the committee's spec of
// 2026-09-16). One form holds the document and the text of every block.
//
//   Preview   every change re-renders through the ONE renderer on the
//             server (preview_draft: the form's current values, nothing
//             saved) into the iframe, debounced, keeping the reader's
//             scroll position. Desktop is drawn at full width and scaled
//             to the pane; Phone stacks on purpose.
//   Saving    the draft is saved quietly before any block action (add,
//             upload, remove, preview in a new tab, discard), on Cmd+S,
//             and every minute while dirty; a dirty form warns before the
//             tab closes. A failed save is SAID, and the action does not
//             go on (it would reload over unsaved words).
//   Actions   one action form (#block-action) serves every block button:
//             the button carries the URL and method, the builder fills
//             them in on submit. A button carrying data-confirm asks
//             first, through the app's modal.
export default class extends Controller {
  static targets = ["form", "frame", "stage", "pane", "phoneButton", "desktopButton", "status", "saved", "actionForm", "actionMethod"]
  static values = { url: String, delay: { type: Number, default: 500 }, autosave: { type: Number, default: 60000 } }

  static DESKTOP = 700
  static PHONE = 390

  connect() {
    this.width = "desktop"
    this.dirty = false
    this.paint()
    if (this.hasStageTarget && window.ResizeObserver) {
      this.resizer = new ResizeObserver(() => this.paint())
      this.resizer.observe(this.stageTarget)
    }
    this.onInput = () => { this.dirty = true; this.schedule() }
    this.formTarget.addEventListener("input", this.onInput)
    this.formTarget.addEventListener("change", this.onInput)
    // A photo drag saves its order through the sortable controller, not the
    // form, so the preview would never hear about it: redraw when it lands.
    this.onSorted = () => this.refresh()
    this.element.addEventListener("sortable:sorted", this.onSorted)
    this.onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); this.save() }
    }
    document.addEventListener("keydown", this.onKey)
    this.onUnload = (e) => { if (this.dirty) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", this.onUnload)
    // The main form's own submit (Save draft, Next) is a real navigation.
    this.onSubmit = () => { this.dirty = false }
    this.formTarget.addEventListener("submit", this.onSubmit)
    this.autosaveTimer = setInterval(() => { if (this.dirty) this.save() }, this.autosaveValue)
    if (location.hash.startsWith("#block-")) {
      const row = document.querySelector(location.hash)
      if (row) row.scrollIntoView({ block: "center" })
    }
  }

  disconnect() {
    clearTimeout(this.timer)
    clearInterval(this.autosaveTimer)
    if (this.resizer) this.resizer.disconnect()
    this.formTarget.removeEventListener("input", this.onInput)
    this.formTarget.removeEventListener("change", this.onInput)
    this.formTarget.removeEventListener("submit", this.onSubmit)
    this.element.removeEventListener("sortable:sorted", this.onSorted)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("beforeunload", this.onUnload)
  }

  // ---- Preview -------------------------------------------------------------

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.refresh(), this.delayValue)
  }

  async refresh() {
    const body = new FormData(this.formTarget)
    // The form carries _method=patch for its own save; the preview is a
    // plain POST, and Rails would route a PATCH to nowhere.
    body.delete("_method")
    this.say("Updating preview")
    try {
      const res = await fetch(this.urlValue, {
        method: "POST", body, headers: { "X-CSRF-Token": this.token(), Accept: "text/html" }, credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      // Keep her place: a swap of srcdoc lands at the top otherwise, and the
      // tour dates live at the bottom of a long document.
      const y = this.frameTarget.contentWindow ? this.frameTarget.contentWindow.scrollY : 0
      this.frameTarget.addEventListener("load", () => this.frameTarget.contentWindow.scrollTo(0, y), { once: true })
      this.frameTarget.srcdoc = html
      this.say("Preview is current")
    } catch (e) {
      this.say("Preview could not update; your draft is unaffected")
    }
  }

  // ---- Saving --------------------------------------------------------------

  // The quiet save. Resolves true when the draft is saved.
  async save() {
    const body = new FormData(this.formTarget)
    body.set("commit_to", "save")
    try {
      const res = await fetch(this.formTarget.action, {
        method: "POST", body, credentials: "same-origin", headers: { "X-CSRF-Token": this.token(), Accept: "text/html" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      this.dirty = false
      if (this.hasSavedTarget) this.savedTarget.textContent = `Saved ${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
      return true
    } catch (e) {
      if (this.hasSavedTarget) this.savedTarget.textContent = "Could not save. Check your connection and try Save draft."
      return false
    }
  }

  // A block action or a standalone form (preview in a new tab, discard):
  // its own form (a nested form swallows a button_to) that reloads the
  // page. Save the draft first so nothing typed is lost, then let it go.
  async saveThenSubmit(event) {
    event.preventDefault()
    const form = event.currentTarget
    const button = event.submitter
    if (button && button.dataset.url && this.hasActionFormTarget && form === this.actionFormTarget) {
      form.action = button.dataset.url
      this.actionMethodTarget.value = button.dataset.method || "post"
    }
    if (button && button.dataset.confirm && window.appConfirm) {
      const ok = await window.appConfirm(button.dataset.confirm, { confirmLabel: button.dataset.confirmLabel || "Remove", focus: "cancel" })
      if (!ok) return
    }
    if (!(await this.save())) return
    // Native submit: no second submit event, so this handler never re-enters.
    form.submit()
  }

  // ---- Width ---------------------------------------------------------------

  phone(event) {
    event.preventDefault()
    this.width = "phone"
    this.paint()
  }

  desktop(event) {
    event.preventDefault()
    this.width = "desktop"
    this.paint()
  }

  // The document stacks its columns under 640px (its own media query). The
  // Desktop preview must never show that, so the frame is laid out at a
  // real desktop width and scaled down to whatever the pane can hold. The
  // pane is sized to fit under the shell header, so it stays pinned while
  // she edits the columns (owner ruling 2026-09-16: "the preview should
  // scroll with you"); the frame's own scroll is the only scroll.
  paint() {
    const phone = this.width === "phone"
    const { DESKTOP, PHONE } = this.constructor
    const width = phone ? PHONE : DESKTOP
    const room = this.hasStageTarget ? this.stageTarget.clientWidth - 24 : width
    const scale = Math.min(1, room / width)
    const pinnedTop = this.hasPaneTarget ? parseFloat(getComputedStyle(this.paneTarget).top) || 72 : 72
    const paneHeader = 44
    const height = Math.max(480, window.innerHeight - pinnedTop - paneHeader - 24 - 16)
    const frame = this.frameTarget
    frame.style.width = `${width}px`
    frame.style.height = `${height / scale}px`
    frame.style.transform = `scale(${scale})`
    frame.style.marginLeft = phone ? `${Math.max(0, (room - width * scale) / 2)}px` : "0"
    if (this.hasStageTarget) this.stageTarget.style.height = `${height + 24}px`
    if (this.hasPhoneButtonTarget) this.phoneButtonTarget.setAttribute("aria-pressed", phone ? "true" : "false")
    if (this.hasDesktopButtonTarget) this.desktopButtonTarget.setAttribute("aria-pressed", phone ? "false" : "true")
  }

  say(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  token() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
